import React from "react";
import { Bot, Clock, FileText, Image as ImageIcon, Mic, Play, Tag, Video as VideoIcon } from "lucide-react";
import { renderMessageVariables, SAMPLE_CONTACT } from "@/lib/messageVariables";
import { formatWhatsAppText } from "@/lib/whatsappTextFormat";
import { useTheme } from "@/contexts/ThemeContext";
import type { FluxoBlock } from "@/hooks/useFluxos";
import type { AssistantOption } from "./BlockCard";
import { useTranslation } from "react-i18next";

interface FluxoWhatsAppPreviewProps {
  blocks: FluxoBlock[];
  assistants: AssistantOption[];
  fluxoName?: string;
}

const CHAT_BG_LIGHT =
  'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23d1d7db\' fill-opacity=\'0.45\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")';

const CHAT_BG_DARK =
  'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23182229\' fill-opacity=\'0.6\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")';

const MessageMeta = () => (
  <div className="flex items-center justify-end gap-1 mt-1">
    <span className="text-[11px] text-[#667781] dark:text-[#8696a0]">14:32</span>
    <svg className="w-4 h-4 text-[#53bdeb]" viewBox="0 0 16 15" fill="currentColor">
      <path d="M15.01 3.316l-.478-.372a.365.365 0 00-.51.063L8.666 9.88a.32.32 0 01-.484.033l-.358-.325a.32.32 0 00-.484.033l-.378.456a.32.32 0 00.063.439l1.04.94a.397.397 0 00.575-.063l6.004-7.042a.365.365 0 00-.063-.51zm-5.657 0l-.478-.372a.365.365 0 00-.51.063L2.997 9.88a.32.32 0 01-.484.033l-.358-.325a.32.32 0 00-.484.033l-.378.456a.32.32 0 00.063.439l1.04.94a.397.397 0 00.575-.063l6.004-7.042a.365.365 0 00-.063-.51z" />
    </svg>
  </div>
);

const Bubble: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="max-w-[85%] ml-auto">
    <div className="bg-[#d9fdd3] rounded-lg overflow-hidden shadow-sm dark:bg-[#005c4b]">
      {children}
    </div>
  </div>
);

const CenterPill: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex justify-center max-w-full px-2">
    <div className="bg-white/90 rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5 text-[11.5px] text-[#667781] shadow-sm max-w-full min-w-0 dark:bg-[#182229] dark:text-[#8696a0]">
      {children}
    </div>
  </div>
);

const FakeWaveform = () => (
  <div className="flex items-center gap-[2px] flex-1 h-[24px]">
    {[8, 14, 10, 18, 12, 20, 9, 16, 11, 19, 13, 8, 15, 10, 17, 12, 9, 14, 11, 16, 8, 13, 10, 15].map(
      (h, i) => (
        <span
          key={i}
          className="w-[2.5px] rounded-full bg-[#7d9c95]"
          style={{ height: `${h}px` }}
        />
      )
    )}
  </div>
);

const BlockMessage: React.FC<{ block: FluxoBlock; assistants: AssistantOption[] }> = ({
  block,
  assistants,
}) => {
  const { t } = useTranslation("automations");
  switch (block.type) {
    case "texto":
      return (
        <Bubble>
          <div className="px-2.5 pt-2 pb-1.5">
            <p className="text-[13.5px] text-[#111b21] leading-[19px] whitespace-pre-wrap dark:text-[#e9edef]">
              {formatWhatsAppText(
                block.content
                  ? renderMessageVariables(block.content, SAMPLE_CONTACT)
                  : "Mensagem de texto..."
              )}
            </p>
            <MessageMeta />
          </div>
        </Bubble>
      );
    case "imagem":
      return (
        <Bubble>
          {block.url ? (
            <div className="w-full max-h-[180px] overflow-hidden">
              <img src={block.url} alt={t("flow.flowImage")} className="w-full h-full object-cover" />
            </div>
          ) : (
            <div className="w-full h-28 bg-[#c5e6c0] flex flex-col items-center justify-center gap-1.5 dark:bg-[#0b4a3f]">
              <ImageIcon className="w-7 h-7 text-[#54656f]/70 dark:text-white/60" />
              <span className="text-xs text-[#54656f]/70 dark:text-white/50">{t("flow.blockImage")}</span>
            </div>
          )}
          <div className="px-2.5 pt-1 pb-1.5">
            <MessageMeta />
          </div>
        </Bubble>
      );
    case "video":
      return (
        <Bubble>
          <div className="relative w-full h-28 bg-[#c5e6c0] flex flex-col items-center justify-center gap-1.5 dark:bg-[#0b4a3f]">
            {block.url ? (
              <>
                <video
                  src={block.url}
                  muted
                  playsInline
                  preload="metadata"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="relative w-11 h-11 rounded-full bg-black/45 flex items-center justify-center">
                  <Play className="w-5 h-5 text-white" fill="currentColor" />
                </div>
              </>
            ) : (
              <>
                <VideoIcon className="w-7 h-7 text-[#54656f]/70 dark:text-white/60" />
                <span className="text-xs text-[#54656f]/70 dark:text-white/50">
                  {t("flow.blockVideo")}
                </span>
              </>
            )}
          </div>
          <div className="px-2.5 pt-1 pb-1.5">
            <MessageMeta />
          </div>
        </Bubble>
      );
    case "arquivo":
      return (
        <Bubble>
          <div className="px-2 pt-2 pb-1.5 w-[250px]">
            <div className="flex items-center gap-2.5 rounded-lg bg-black/5 px-2.5 py-2 dark:bg-black/20">
              <FileText className="w-6 h-6 text-[#54656f] shrink-0 dark:text-[#8696a0]" />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] text-[#111b21] truncate dark:text-[#e9edef]">
                  {block.fileName || t("flow.blockFile")}
                </p>
                <p className="text-[11px] text-[#667781] uppercase dark:text-[#8696a0]">
                  {block.fileName?.split(".").pop() || "—"}
                </p>
              </div>
            </div>
            <MessageMeta />
          </div>
        </Bubble>
      );
    case "audio":
      return (
        <Bubble>
          <div className="px-2.5 pt-2.5 pb-1.5 w-[230px]">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#d1d7db] flex items-center justify-center shrink-0 dark:bg-[#2a3942]">
                <Mic className="w-4 h-4 text-[#54656f] dark:text-[#8696a0]" />
              </div>
              <Play className="w-5 h-5 text-[#54656f] shrink-0 dark:text-[#8696a0]" fill="currentColor" />
              <FakeWaveform />
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-[11px] text-[#667781] dark:text-[#8696a0]">0:12</span>
              <MessageMeta />
            </div>
          </div>
        </Bubble>
      );
    case "delay":
      return (
        <CenterPill>
          <Clock className="w-3.5 h-3.5" />
          Aguardar {block.seconds} {block.seconds === 1 ? "segundo" : "segundos"}
        </CenterPill>
      );
    case "assistente": {
      const assistant = assistants.find((a) => a.id === block.assistantId);
      return (
        <CenterPill>
          <Bot className="w-3.5 h-3.5 text-[#00a884] shrink-0" />
          <span className="truncate min-w-0">
            {assistant ? `Assistente "${assistant.name}" ativado` : "Selecione um assistente"}
          </span>
        </CenterPill>
      );
    }
    case "tag":
      return (
        <CenterPill>
          <Tag className="w-3.5 h-3.5 text-[#2563EB] shrink-0" />
          <span className="truncate min-w-0 capitalize">
            {block.tags.length > 0
              ? t("flow.tagPreview", { tags: block.tags.join(", ") })
              : t("flow.tagPreviewEmpty")}
          </span>
        </CenterPill>
      );
  }
};

export const FluxoWhatsAppPreview: React.FC<FluxoWhatsAppPreviewProps> = ({
  blocks,
  assistants,
  fluxoName,
}) => {
  const { t } = useTranslation("automations");
  const { isDark } = useTheme();

  return (
    <div className="flex flex-col items-center">
      <div className="w-[320px] bg-white rounded-2xl overflow-hidden border border-[#e9edef] shadow-2xl dark:bg-[#0b141a] dark:border-neutral-700">
        <div className="bg-[#f0f2f5] px-4 py-3 flex items-center gap-3 dark:bg-[#202c33]">
          <div className="w-8 h-8 rounded-full bg-[#dfe5e7] flex items-center justify-center dark:bg-[#2a3942]">
            <svg className="w-4 h-4 text-[#54656f] dark:text-[#8696a0]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#111b21] truncate dark:text-[#e9edef]">
              {fluxoName || "+55 11 99999-0000"}
            </p>
            <p className="text-[11px] text-[#667781] dark:text-[#8696a0]">online</p>
          </div>
        </div>

        <div
          className="px-3 py-4 min-h-[320px] max-h-[480px] overflow-y-auto flex flex-col gap-2 bg-[#efeae2] dark:bg-[#0b141a]"
          style={{ backgroundImage: isDark ? CHAT_BG_DARK : CHAT_BG_LIGHT }}
        >
          {blocks.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-[12px] text-[#667781] text-center px-6 dark:text-[#8696a0]">
                {t("flow.previewEmpty")}
              </p>
            </div>
          ) : (
            blocks.map((block) => (
              <BlockMessage key={block.id} block={block} assistants={assistants} />
            ))
          )}
        </div>

        <div className="bg-[#f0f2f5] px-2 py-2 flex items-center gap-2 dark:bg-[#202c33]">
          <div className="flex-1 bg-white rounded-full px-4 py-2 dark:bg-[#2a3942]">
            <span className="text-[13px] text-[#667781] dark:text-[#8696a0]">{t("flow.message")}</span>
          </div>
          <div className="w-9 h-9 rounded-full bg-[#00a884] flex items-center justify-center">
            <svg className="w-5 h-5 text-white dark:text-[#0b141a]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2a10 10 0 1010 10A10 10 0 0012 2zm0 18a8 8 0 118-8 8 8 0 01-8 8zm4-9h-3V8a1 1 0 00-2 0v3H8a1 1 0 000 2h3v3a1 1 0 002 0v-3h3a1 1 0 000-2z" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FluxoWhatsAppPreview;
